import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import { EmptyState } from '../../../styles/feedback/EmptyState';
import { Container } from '../../../styles/layout/Container';
import { PageHeader, PageHeaderText, PageTitle, PageSubtitle } from '../../../styles/layout/PageHeader';
import { Section, SectionBody, SectionHeader, SectionTitle } from '../../../styles/layout/Section';
import { ActionBar, ActionBarLeft, ActionBarRight } from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import axios from '../../../services/axios';

// ─── Estilos locais ───────────────────────────────────────────────────────────

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $isActive }) =>
    $isActive ? 'rgba(46,125,50,0.1)' : 'rgba(0,0,0,0.06)'};
  border: 1px solid ${({ $isActive }) =>
    $isActive ? 'rgba(46,125,50,0.35)' : 'rgba(0,0,0,0.15)'};
  color: ${({ $isActive }) => ($isActive ? '#2e7d32' : '#666')};
`;

const TypeBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(25, 118, 210, 0.08);
  border: 1px solid rgba(25, 118, 210, 0.2);
  color: #1565c0;
`;

// ─── Modal de criação / edição ────────────────────────────────────────────────
function ProviderFormModal({ isOpen, providerToEdit, onSuccess, onClose }) {
  const isEditMode = Boolean(providerToEdit);

  const [providerName, setProviderName] = useState(providerToEdit?.provider_name ?? '');
  const [providerType, setProviderType] = useState(providerToEdit?.provider_type ?? '');
  const [cnpjNumber,   setCnpjNumber]   = useState(providerToEdit?.cnpj_number ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  async function handleSubmit() {
    if (!providerName.trim()) { toast.error('Nome do prestador é obrigatório'); return; }
    if (!providerType)        { toast.error('Tipo do prestador é obrigatório'); return; }
    if (providerType === 'PJ' && !cnpjNumber.trim()) {
      toast.error('CNPJ é obrigatório para prestadores do tipo PJ');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await axios.put(`/service-aquisition/providers/${providerToEdit.id}`, {
          provider_name: providerName.trim(),
          provider_type: providerType,
          cnpj_number:   cnpjNumber.trim() || undefined,
        });
        toast.success('Prestador atualizado com sucesso');
      } else {
        await axios.post('/service-aquisition/providers', {
          provider_name: providerName.trim(),
          provider_type: providerType,
          cnpj_number:   cnpjNumber.trim() || undefined,
        });
        toast.success('Prestador cadastrado com sucesso');
      }
      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message
        ?? err.response?.data?.errors?.[0]?.message
        ?? 'Erro ao salvar prestador';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 460, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>
          {isEditMode ? 'Editar Prestador' : 'Novo Prestador'}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Nome *
            </label>
            <input
              type="text"
              placeholder="Razão social ou nome"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              disabled={isSubmitting}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Tipo *
            </label>
            <select
              value={providerType}
              onChange={(e) => setProviderType(e.target.value)}
              disabled={isSubmitting}
              style={{ width: '100%', boxSizing: 'border-box' }}
            >
              <option value="">Selecione...</option>
              <option value="PJ">PJ — Pessoa Jurídica</option>
              <option value="PF">PF — Pessoa Física</option>
              <option value="ESTRANGEIRO">Estrangeiro</option>
            </select>
          </div>

          {providerType === 'PJ' && (
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                CNPJ *
              </label>
              <input
                type="text"
                placeholder="XX.XXX.XXX/XXXX-XX"
                maxLength={18}
                value={cnpjNumber}
                onChange={(e) => setCnpjNumber(e.target.value)}
                disabled={isSubmitting}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
          <button type="button" onClick={onClose} disabled={isSubmitting}>Cancelar</button>
          <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Cadastrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de confirmação de desativação ──────────────────────────────────────
function DeactivateConfirmationModal({ isOpen, provider, isSubmitting, onConfirm, onCancel }) {
  if (!isOpen || !provider) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>
          {provider.is_active ? 'Desativar prestador?' : 'Reativar prestador?'}
        </h2>
        <p style={{ margin: '0 0 8px', color: '#555', fontSize: 14 }}>
          <strong>{provider.provider_name}</strong>
        </p>
        <p style={{ margin: '0 0 24px', color: '#777', fontSize: 13 }}>
          {provider.is_active
            ? 'O prestador não aparecerá mais nas pesquisas do formulário, mas o histórico de serviços tomados será preservado.'
            : 'O prestador voltará a aparecer nas pesquisas do formulário.'}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={isSubmitting}>Cancelar</button>
          <button type="button" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting
              ? 'Aguarde...'
              : provider.is_active ? 'Confirmar Desativação' : 'Confirmar Reativação'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatProviderType(providerType) {
  const labels = { PJ: 'Pessoa Jurídica', PF: 'Pessoa Física', ESTRANGEIRO: 'Estrangeiro' };
  return labels[providerType] ?? providerType;
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ServiceAquisitionProviderList() {
  // ─── Filtros ──────────────────────────────────────────────────────────────
  const [filterSearch,       setFilterSearch]       = useState('');
  const [filterProviderType, setFilterProviderType] = useState('');
  const [filterIsActive,     setFilterIsActive]     = useState('1'); // padrão: ativos

  // ─── Resultados ───────────────────────────────────────────────────────────
  const [providers,    setProviders]    = useState([]);
  const [isLoading,    setIsLoading]    = useState(false);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // ─── Modais ───────────────────────────────────────────────────────────────
  const [isFormModalOpen,         setIsFormModalOpen]         = useState(false);
  const [providerToEdit,          setProviderToEdit]          = useState(null);
  const [isDeactivateModalOpen,   setIsDeactivateModalOpen]   = useState(false);
  const [providerToToggle,        setProviderToToggle]        = useState(null);
  const [isTogglingActive,        setIsTogglingActive]        = useState(false);

  const ITEMS_PER_PAGE = 20;

  // ─── Busca na API ─────────────────────────────────────────────────────────
  const fetchProviders = useCallback(async (pageToFetch = 1) => {
    setIsLoading(true);
    try {
      const params = { page: pageToFetch, limit: ITEMS_PER_PAGE };

      if (filterSearch.trim()) params.search        = filterSearch.trim();
      if (filterProviderType)  params.provider_type = filterProviderType;

      // is_active: '1' = ativos, '0' = inativos, 'all' = todos
      params.is_active = filterIsActive;

      const response = await axios.get('/service-aquisition/providers', { params });
      setProviders(response.data.data.providers);
      setTotalPages(response.data.meta.total_pages);
      setTotalRecords(response.data.meta.total);
      setCurrentPage(pageToFetch);
    } catch (err) {
      const errorMessage = err.response?.data?.message ?? 'Erro ao buscar prestadores';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [filterSearch, filterProviderType, filterIsActive]);

  function handleSearchSubmit(event) {
    event.preventDefault();
    fetchProviders(1);
  }

  function handleClearFilters() {
    setFilterSearch('');
    setFilterProviderType('');
    setFilterIsActive('1');
    setProviders([]);
    setTotalPages(1);
    setTotalRecords(0);
    setCurrentPage(1);
  }

  // ─── Abrir edição ─────────────────────────────────────────────────────────
  function handleEditClick(provider) {
    setProviderToEdit(provider);
    setIsFormModalOpen(true);
  }

  // ─── Abrir toggle ativo/inativo ───────────────────────────────────────────
  function handleToggleActiveClick(provider) {
    setProviderToToggle(provider);
    setIsDeactivateModalOpen(true);
  }

  // ─── Confirmar toggle ativo/inativo ──────────────────────────────────────
  async function handleToggleActiveConfirm() {
    if (!providerToToggle) return;
    setIsTogglingActive(true);
    try {
      await axios.put(`/service-aquisition/providers/${providerToToggle.id}`, {
        is_active: providerToToggle.is_active ? 0 : 1,
      });
      const actionLabel = providerToToggle.is_active ? 'desativado' : 'reativado';
      toast.success(`Prestador ${actionLabel} com sucesso`);
      setIsDeactivateModalOpen(false);
      setProviderToToggle(null);
      fetchProviders(currentPage);
    } catch (err) {
      toast.error(err.response?.data?.message ?? 'Erro ao alterar status do prestador');
    } finally {
      setIsTogglingActive(false);
    }
  }

  const hasResults = providers.length > 0;

  return (
    <Container>
      <Loading isLoading={isLoading} />

      <ProviderFormModal
        isOpen={isFormModalOpen}
        providerToEdit={providerToEdit}
        onSuccess={() => fetchProviders(currentPage)}
        onClose={() => { setIsFormModalOpen(false); setProviderToEdit(null); }}
      />

      <DeactivateConfirmationModal
        isOpen={isDeactivateModalOpen}
        provider={providerToToggle}
        isSubmitting={isTogglingActive}
        onConfirm={handleToggleActiveConfirm}
        onCancel={() => { setIsDeactivateModalOpen(false); setProviderToToggle(null); }}
      />

      <PageHeader>
        <PageHeaderText>
          <PageTitle>Prestadores de Serviço</PageTitle>
          <PageSubtitle>Cadastro e gestão dos prestadores utilizados nos serviços tomados</PageSubtitle>
        </PageHeaderText>
      </PageHeader>

      {/* ─── Filtros ──────────────────────────────────────────────────── */}
      <Section>
        <SectionHeader><SectionTitle>Filtros</SectionTitle></SectionHeader>
        <SectionBody>
          <form onSubmit={handleSearchSubmit}>
            <FormGrid>
              <Field className="col-4">
                <label htmlFor="filter-search">Nome ou CNPJ</label>
                <input
                  id="filter-search"
                  type="text"
                  placeholder="Buscar por nome ou CNPJ"
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                />
              </Field>

              <Field className="col-4">
                <label htmlFor="filter-provider-type">Tipo</label>
                <select
                  id="filter-provider-type"
                  value={filterProviderType}
                  onChange={(e) => setFilterProviderType(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="PJ">PJ — Pessoa Jurídica</option>
                  <option value="PF">PF — Pessoa Física</option>
                  <option value="ESTRANGEIRO">Estrangeiro</option>
                </select>
              </Field>

              <Field className="col-4">
                <label htmlFor="filter-is-active">Status</label>
                <select
                  id="filter-is-active"
                  value={filterIsActive}
                  onChange={(e) => setFilterIsActive(e.target.value)}
                >
                  <option value="1">Ativos</option>
                  <option value="0">Inativos</option>
                  <option value="all">Todos</option>
                </select>
              </Field>
            </FormGrid>

            <ActionBar>
              <ActionBarLeft />
              <ActionBarRight>
                <button type="button" onClick={handleClearFilters} disabled={isLoading}>
                  Limpar
                </button>
                <button type="submit" disabled={isLoading}>
                  Pesquisar
                </button>
                <button
                  type="button"
                  onClick={() => { setProviderToEdit(null); setIsFormModalOpen(true); }}
                  disabled={isLoading}
                >
                  + Novo Prestador
                </button>
              </ActionBarRight>
            </ActionBar>
          </form>
        </SectionBody>
      </Section>

      {/* ─── Resultados ───────────────────────────────────────────────── */}
      <Section>
        <SectionHeader>
          <SectionTitle>
            Resultados {totalRecords > 0 && `(${totalRecords})`}
          </SectionTitle>
        </SectionHeader>
        <SectionBody>
          {hasResults ? (
            <>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>CNPJ</th>
                      <th className="center">Status</th>
                      <th className="center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providers.map((provider) => (
                      <tr key={provider.id}>
                        <td>{provider.id}</td>
                        <td>{provider.provider_name}</td>
                        <td><TypeBadge>{formatProviderType(provider.provider_type)}</TypeBadge></td>
                        <td>{provider.cnpj_number ?? '—'}</td>
                        <td className="center">
                          <StatusBadge $isActive={provider.is_active}>
                            {provider.is_active ? 'Ativo' : 'Inativo'}
                          </StatusBadge>
                        </td>
                        <td className="center">
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleEditClick(provider)}
                              style={{ fontSize: 12, padding: '3px 10px' }}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleActiveClick(provider)}
                              style={{ fontSize: 12, padding: '3px 10px' }}
                            >
                              {provider.is_active ? 'Desativar' : 'Reativar'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              <PaginationBar
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => fetchProviders(page)}
                disabled={isLoading}
              />
            </>
          ) : (
            <EmptyState
              title="Nenhum prestador encontrado"
              description="Use os filtros acima e clique em Pesquisar, ou cadastre um novo prestador."
            />
          )}
        </SectionBody>
      </Section>
    </Container>
  );
}
